import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Video, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import api, { errMsg } from "@/lib/api";
import { MeetingRoom, SA_LOGO } from "@/components/meeting/MeetingRoom";
import { usePageMeta } from "@/lib/seo";

const Dots = ({ step }) => (
  <div className="flex items-center justify-center gap-2" data-testid="guest-steps">{[1, 2, 3].map(n => (
    <span key={n} className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${n < step ? "bg-emerald-500 text-white" : n === step ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>{n < step ? <Check className="w-3.5 h-3.5" /> : n}</span>))}</div>
);

export default function GuestMeetPage() {
  const { code } = useParams();
  const [qs] = useSearchParams();
  usePageMeta("Join meeting");
  const [conf, setConf] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ name: qs.get("n") || "", email: qs.get("e") || "", phone: "", password: "" });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(false);
  const [joined, setJoined] = useState(false);
  useEffect(() => { api.get(`/api/rtc/guest/${code}`).then(r => setConf(r.data)).catch(e => setErr(e.response?.data?.detail || "That meeting link isn't valid")); }, [code]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  if (joined) return <MeetingRoom confId={conf.id} joinBody={{ name: f.name, guest_code: code, email: f.email }} onLeave={() => { setJoined(false); window.location.reload(); }} />;
  const mode = conf?.signup || "off";
  const next = async () => {
    setErr("");
    if (mode === "off") return setJoined(true);
    if (step === 1) return setStep(2);
    setBusy(true);
    let known = existing;
    try {
      if (mode === "customer") {
        if (known) await api.post("/api/auth/login", { email: f.email.trim(), password: f.password });
        else {
          try { await api.post("/api/auth/register", { name: f.name.trim(), email: f.email.trim(), password: f.password }); }
          catch (e) {
            if (!/already registered/i.test(e.response?.data?.detail || "")) throw e;
            known = true; setExisting(true);
            await api.post("/api/auth/login", { email: f.email.trim(), password: f.password });
          }
        }
      } else {
        await api.post(`/api/rtc/guest/${code}/rep-signup`, { name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() });
      }
      setStep(3);
    } catch (e) { setErr(known ? "That password doesn't match your account. Try again or reset it from the login page." : errMsg(e, "Couldn't create your account")); }
    finally { setBusy(false); }
  };
  const ok1 = f.name.trim() && (mode === "off" || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()));
  const ok2 = mode === "customer" ? f.password.length >= 6 : true;
  const btn = "w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50";
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="guest-meet-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 space-y-5">
        <img src={SA_LOGO} alt="The ShopAdvisor" className="h-14 mx-auto" />
        {!conf && err ? <p className="text-center text-red-600" data-testid="guest-meet-error">{err}</p> : !conf ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /> : (
          <>
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-slate-400">You're invited to</p>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="guest-meet-title">{conf.title}</h1>
              <p className="text-sm text-slate-500">Hosted by {conf.host_name}{conf.status === "ended" ? "" : conf.status !== "active" ? " · waiting for the host to start" : ""}</p>
            </div>
            {conf.status === "ended" ? <p className="bg-slate-100 rounded-xl p-4 text-center text-slate-700 font-semibold" data-testid="guest-meet-ended">This meeting has ended. Thanks for joining!</p> : <>
              {mode !== "off" && <><Dots step={step} /><p className="text-center text-xs text-slate-500" data-testid="guest-signup-note">{mode === "customer" ? "Create your free account in 3 quick steps — you'll also see your quotes and meetings anytime." : "Sign up as a ShopAdvisor sales rep or affiliate in 3 quick steps."}</p></>}
              {step === 1 && <div className="space-y-2">
                <Input value={f.name} onChange={set("name")} placeholder="Your name" data-testid="guest-meet-name" />
                {mode !== "off" && <Input type="email" value={f.email} onChange={set("email")} placeholder="Email" data-testid="guest-meet-email" />}
                <button onClick={next} disabled={!ok1} className={btn} data-testid="guest-meet-join">{mode === "off" ? <><Video className="w-5 h-5" />Join meeting</> : "Next"}</button>
              </div>}
              {step === 2 && <div className="space-y-2">
                {mode === "customer" ? <Input type="password" value={f.password} onChange={set("password")} onKeyDown={e => e.key === "Enter" && ok2 && next()} placeholder={existing ? "Your password" : "Create a password (6+ characters)"} data-testid="guest-meet-password" />
                  : <Input type="tel" value={f.phone} onChange={set("phone")} placeholder="Mobile phone (optional)" data-testid="guest-meet-phone" />}
                <button onClick={next} disabled={!ok2 || busy} className={btn} data-testid="guest-meet-signup">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "customer" ? (existing ? "Sign in" : "Create account") : "Sign up"}</button>
                <button onClick={() => setStep(1)} className="w-full text-xs text-slate-500" data-testid="guest-meet-back">Back</button>
              </div>}
              {step === 3 && <div className="space-y-3 text-center">
                <p className="text-sm text-emerald-700 font-semibold" data-testid="guest-meet-done">{mode === "customer" ? (existing ? "Welcome back! You're signed in." : "Your account is ready!") : "Thanks for signing up! We'll be in touch about next steps."}</p>
                <button onClick={() => setJoined(true)} className={btn} data-testid="guest-meet-enter"><Video className="w-5 h-5" />Join meeting</button>
              </div>}
              {err && <p className="text-center text-sm text-red-600" data-testid="guest-meet-error">{err}</p>}
            </>}
            <p className="text-xs text-slate-400 text-center">Works on phones, tablets and computers. Allow camera and microphone when your browser asks.</p>
          </>
        )}
      </div>
    </div>
  );
}
